import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Layers, List, X, Leaf, Loader2, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { renderSenderNoteMarkup } from '../utils/senderNoteMarkup';

/**
 * Share picker. Presents three options for outbound sharing — each ALWAYS
 * produces a PNG with a QR code + a human-readable short link baked into
 * the footer:
 *   1. Single plant, all details, small QR
 *   2. All plants, minimal info, QR
 *   3. All plants, names only, QR
 *
 * The heavy lifting is done by the caller-provided `onShare` callback so
 * this component stays presentational.
 */
export const ShareChoiceModal = ({ open, onClose, plant = null, onShare }) => {
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === 'dark';
  // `plant` present ⇒ opened from PlantDetailModal → default to Option 1.
  // No plant ⇒ opened from LibraryPage header → Option 1 is disabled with
  // a helper tooltip, default to Option 2.
  const [choice, setChoice] = useState(() => (plant ? 'plant-full' : 'library-minimal'));
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // {done,total} — full-detail export
  // Optional short greeting the sender types in — rendered above the
  // plant grid on the recipient's Grow-Along page. Capped at 240 chars
  // matching the payload cap so we surface the trim before the user
  // hits Share.
  const NOTE_MAX = 240;
  const [note, setNote] = useState('');
  const noteRef = useRef(null);

  // Wrap the currently-selected text with a marker (or insert an empty
  // pair at the cursor if nothing is selected). Keeps the textarea
  // focused and places the cursor inside the wrapper so users can keep
  // typing. Never expands beyond NOTE_MAX.
  const applyMarker = (marker) => {
    const ta = noteRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? note.length;
    const end = ta.selectionEnd ?? note.length;
    const before = note.slice(0, start);
    const selected = note.slice(start, end);
    const after = note.slice(end);
    const next = `${before}${marker}${selected}${marker}${after}`.slice(0, NOTE_MAX);
    setNote(next);
    // Restore cursor: after the opening marker if empty, else after the closing marker.
    const nextCursor = selected.length === 0
      ? start + marker.length
      : end + marker.length * 2;
    requestAnimationFrame(() => {
      if (noteRef.current) {
        noteRef.current.focus();
        noteRef.current.setSelectionRange(nextCursor, nextCursor);
      }
    });
  };

  React.useEffect(() => {
    // Reset when the dialog re-opens for a different plant or context.
    if (open) {
      setChoice(plant ? 'plant-full' : 'library-minimal');
      setNote('');
      setProgress(null);
    }
  }, [open, plant]);

  const handleShare = async () => {
    if (busy) return;
    setBusy(true);
    setProgress(null);
    try {
      await onShare({
        choice,
        format: 'image',
        plant,
        note: note.trim() || null,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Could not create share');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const cards = [
    {
      id: 'plant-full',
      icon: <Leaf className="w-5 h-5" aria-hidden="true" />,
      title: plant ? `Just this plant, in detail` : 'One plant, in detail',
      desc: plant
        ? `${plant.common_name || 'This plant'} with every care field, plus a short link the receiver can tap or type.`
        : 'Open a plant from your library to share just that one with all its care details.',
      disabled: !plant,
    },
    {
      id: 'library-minimal',
      icon: <Layers className="w-5 h-5" aria-hidden="true" />,
      title: 'My library, at a glance',
      desc: 'A clean PDF with photos + common & scientific names for every plant, plus a scannable QR code and short link to the live library.',
      disabled: false,
    },
    {
      id: 'library-full',
      icon: <FileText className="w-5 h-5" aria-hidden="true" />,
      title: 'My library, in full detail',
      desc: 'A multi-page PDF with one page per plant — photo and every care field — plus a scannable QR code and short link to the live library.',
      disabled: false,
    },
    {
      id: 'library-names',
      icon: <List className="w-5 h-5" aria-hidden="true" />,
      title: 'Just the names, no details',
      desc: 'A clean PDF list of your plants\u2019 common names, plus a scannable QR code and short link for anyone who wants the full details.',
      disabled: false,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-lg"
        style={{ backgroundColor: 'white' }}
        data-testid="share-choice-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-display" style={{ color: 'hsl(143, 28%, 25%)' }}>
            How would you like to share?
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choose a share format: a single plant with all details, the whole library at a glance (photos + names), the whole library in full detail (one page per plant), or just the plant names. Library options produce a PDF with a QR code and short link.
          </DialogDescription>
          <Link
            to="/about#sharing"
            className="text-xs underline hover:opacity-80 w-fit"
            style={{ color: 'hsl(143, 28%, 40%)' }}
            data-testid="share-learn-more"
          >
            How sharing works
          </Link>
        </DialogHeader>

        <div className="space-y-2 py-2" role="radiogroup" aria-label="Share format">
          {cards.map((c) => {
            const selected = choice === c.id;
            const disabled = c.disabled;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => !disabled && setChoice(c.id)}
                disabled={disabled}
                role="radio"
                aria-checked={selected}
                data-testid={`share-choice-${c.id}`}
                className="w-full text-left rounded-xl p-3 flex items-start gap-3 transition-colors"
                style={{
                  backgroundColor: selected
                    ? (isDark ? 'hsl(143, 20%, 22%)' : 'hsl(143, 28%, 96%)')
                    : disabled
                      ? (isDark ? 'hsl(220, 8%, 18%)' : 'hsl(0, 0%, 97%)')
                      : (isDark ? 'hsl(220, 10%, 14%)' : 'white'),
                  border: `2px solid ${selected
                    ? (isDark ? 'hsl(143, 30%, 55%)' : 'hsl(143, 28%, 50%)')
                    : (isDark ? 'hsl(220, 8%, 28%)' : 'hsl(143, 28%, 88%)')}`,
                  opacity: disabled ? (isDark ? 0.7 : 0.55) : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                <div
                  className="flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{
                    width: 36,
                    height: 36,
                    backgroundColor: selected
                      ? (isDark ? 'hsl(143, 30%, 55%)' : 'hsl(143, 28%, 25%)')
                      : (isDark ? 'hsl(220, 8%, 24%)' : 'hsl(143, 28%, 92%)'),
                    color: selected ? 'white' : (isDark ? 'hsl(143, 40%, 78%)' : 'hsl(143, 28%, 25%)'),
                  }}
                >
                  {c.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm" style={{ color: isDark ? 'hsl(143, 40%, 82%)' : 'hsl(143, 28%, 25%)' }}>
                    {c.title}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: isDark ? 'hsl(210, 12%, 72%)' : 'hsl(125, 12%, 35%)' }}>
                    {c.desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Format toggle removed (iter 97) — always share as image. The
            QR-code footer already has a short link underneath for people
            who prefer typing over scanning. */}

        {/* Optional sender welcome note — shown above the plant grid on
            the recipient's Grow-Along page. Keeps sharing conversational
            without turning this into a full compose window. */}
        <div className="pt-4 space-y-1.5" data-testid="share-note-section">
          <label
            htmlFor="share-note-input"
            className="text-xs font-medium block"
            style={{ color: isDark ? 'hsl(143, 40%, 78%)' : 'hsl(143, 28%, 30%)' }}
          >
            Add a personal note (optional)
          </label>
          {/* Formatting toolbar — quick-taps for touch users who don't
              want to type the *bold* / _italic_ markers manually.
              Wraps or unwraps the current selection (or inserts an
              empty pair at the cursor if nothing is selected). */}
          <div
            className="flex items-center gap-1"
            role="toolbar"
            aria-label="Formatting"
            data-testid="share-note-toolbar"
          >
            <button
              type="button"
              onClick={() => applyMarker('*')}
              className="text-xs font-bold w-7 h-7 rounded flex items-center justify-center transition-colors"
              style={{
                backgroundColor: isDark ? 'hsl(220, 8%, 22%)' : 'hsl(143, 28%, 94%)',
                color: isDark ? 'hsl(210, 12%, 88%)' : 'hsl(143, 28%, 25%)',
                border: `1px solid ${isDark ? 'hsl(220, 8%, 32%)' : 'hsl(143, 28%, 82%)'}`,
              }}
              aria-label="Bold"
              title="Bold — wraps selection with * (Ctrl/⌘+B)"
              data-testid="share-note-toolbar-bold"
            >
              B
            </button>
            <button
              type="button"
              onClick={() => applyMarker('_')}
              className="text-xs italic w-7 h-7 rounded flex items-center justify-center transition-colors"
              style={{
                backgroundColor: isDark ? 'hsl(220, 8%, 22%)' : 'hsl(143, 28%, 94%)',
                color: isDark ? 'hsl(210, 12%, 88%)' : 'hsl(143, 28%, 25%)',
                border: `1px solid ${isDark ? 'hsl(220, 8%, 32%)' : 'hsl(143, 28%, 82%)'}`,
                fontFamily: 'serif',
              }}
              aria-label="Italic"
              title="Italic — wraps selection with _ (Ctrl/⌘+I)"
              data-testid="share-note-toolbar-italic"
            >
              I
            </button>
          </div>
          <textarea
            id="share-note-input"
            ref={noteRef}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
            onKeyDown={(e) => {
              // Ctrl/Cmd+B / +I — toggle bold / italic markers around
              // the current selection so keyboard-heavy senders don't
              // have to reach for the toolbar. Uses the same
              // `applyMarker` helper as the toolbar buttons so we get
              // identical wrapping, cursor restoration, and NOTE_MAX
              // enforcement for free.
              if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
              const k = e.key.toLowerCase();
              if (k === 'b') {
                e.preventDefault();
                applyMarker('*');
              } else if (k === 'i') {
                e.preventDefault();
                applyMarker('_');
              }
            }}
            placeholder="Say hi, share what inspired you, tag a favourite…"
            rows={2}
            maxLength={NOTE_MAX}
            className="w-full rounded-lg px-3 py-2 text-sm resize-none"
            style={{
              backgroundColor: isDark ? 'hsl(220, 8%, 20%)' : 'white',
              border: `1px solid ${isDark ? 'hsl(220, 8%, 32%)' : 'hsl(143, 28%, 82%)'}`,
              color: isDark ? 'hsl(210, 12%, 88%)' : 'hsl(125, 12%, 20%)',
            }}
            data-testid="share-note-input"
          />
          <div
            className="text-[11px] flex items-center justify-between gap-2"
            style={{ color: isDark ? 'hsl(210, 12%, 60%)' : 'hsl(125, 12%, 50%)' }}
          >
            <span data-testid="share-note-hint">
              Formatting: *bold*, _italic_
            </span>
            <span
              data-testid="share-note-counter"
              aria-live="polite"
              style={{
                color: note.length >= NOTE_MAX
                  ? 'hsl(0, 70%, 55%)'
                  : (NOTE_MAX - note.length) <= 40
                    ? 'hsl(30, 82%, 44%)'
                    : (isDark ? 'hsl(210, 12%, 60%)' : 'hsl(125, 12%, 50%)'),
                fontWeight: (NOTE_MAX - note.length) <= 40 ? 600 : 400,
              }}
            >
              {note.length >= NOTE_MAX
                ? 'Limit reached'
                : (NOTE_MAX - note.length) <= 40
                  ? `${NOTE_MAX - note.length} characters left`
                  : `${note.length} / ${NOTE_MAX}`}
            </span>
          </div>

          {/* Live preview — renders the note exactly as friends will see
              it on the GrowAlong page (same soft-tan blockquote, same
              markup parser). Only shown when the user has typed
              something so the modal stays compact for the empty state. */}
          {note.trim().length > 0 && (
            <div className="pt-1" data-testid="share-note-preview-wrap">
              <div
                className="text-[10px] uppercase tracking-wider mb-1 font-medium"
                style={{ color: isDark ? 'hsl(143, 30%, 60%)' : 'hsl(143, 28%, 40%)' }}
              >
                Preview
              </div>
              <blockquote
                className="rounded-lg px-3 py-2 text-sm whitespace-pre-line"
                style={{
                  backgroundColor: isDark ? 'hsl(35, 25%, 18%)' : 'hsl(45, 60%, 94%)',
                  color: isDark ? 'hsl(35, 20%, 82%)' : 'hsl(30, 30%, 20%)',
                  borderLeft: '3px solid hsl(143, 28%, 45%)',
                }}
                data-testid="share-note-preview"
              >
                {renderSenderNoteMarkup(note)}
              </blockquote>
            </div>
          )}
        </div>

        {busy && progress && progress.total > 0 && (
          <div className="pt-3" data-testid="share-progress" aria-live="polite">
            <div
              className="flex items-center justify-between text-[11px] mb-1"
              style={{ color: isDark ? 'hsl(210, 12%, 70%)' : 'hsl(125, 12%, 40%)' }}
            >
              <span>Building your care binder…</span>
              <span data-testid="share-progress-label">
                {progress.done}/{progress.total} ({Math.round((progress.done / progress.total) * 100)}%)
              </span>
            </div>
            <div
              className="w-full h-2 rounded-full overflow-hidden"
              style={{ backgroundColor: isDark ? 'hsl(220, 8%, 24%)' : 'hsl(143, 28%, 90%)' }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={progress.total}
              aria-valuenow={progress.done}
            >
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${Math.max(2, Math.round((progress.done / progress.total) * 100))}%`,
                  backgroundColor: 'hsl(143, 45%, 42%)',
                }}
              />
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={busy}
            style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
            data-testid="share-choice-cancel"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleShare}
            disabled={busy || cards.find((c) => c.id === choice)?.disabled}
            style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
            data-testid="share-choice-submit"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {progress && progress.total > 0
                  ? `Building… ${progress.done}/${progress.total}`
                  : 'Preparing…'}
              </>
            ) : (
              <>Share</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareChoiceModal;
